import { gql } from '@apollo/client';

// ── Reusable fragments ──────────────────────────────────────────────────────

export const ROLE_FIELDS = gql`
  fragment RoleFields on Role {
    id
    name
    description
    resourceType
    actions
    isSystem
    isDisabled
    createdAt
    updatedAt
  }
`;

export const MEMBERSHIP_FIELDS = gql`
  fragment MembershipFields on Membership {
    groupId
    groupName
    isDisabled
    addedAt
    addedBy
    roles {
      ...RoleFields
    }
  }
  ${ROLE_FIELDS}
`;

export const USER_FIELDS = gql`
  fragment UserFields on User {
    id
    username
    name
    # Convenience scalar (= emails[0].value), kept for code that only
    # needs a primary email (login greeting, password-reset). Edits go
    # through the typed lists below.
    email
    emails {
      value
      label
    }
    phones {
      value
      label
    }
    avatar
    isDisabled
    isRootAdmin
    createdAt
    updatedAt
    memberships {
      ...MembershipFields
    }
  }
  ${MEMBERSHIP_FIELDS}
`;

export const GROUP_FIELDS = gql`
  fragment GroupFields on Group {
    id
    name
    description
    isDisabled
    createdBy
    memberCount
    resourceCount
    createdAt
    updatedAt
  }
`;

export const REDIRECTION_FIELDS = gql`
  fragment RedirectionFields on RedirectionConfig {
    id
    sourcePath
    targetUrl
    queryParams
    status
    httpCode
    alias
    description
    tagColor
    tagIcon
    baseUrl
    groupIds
    creatorId
    hitCount
    creationDate
    type
    vCardData
  }
`;

// ── Queries ─────────────────────────────────────────────────────────────────

export const GET_USER_PROFILE = gql`
  query GetUserProfile {
    getUserProfile {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const GET_REDIRECTIONS = gql`
  query GetRedirections(
    $page: Int!
    $limit: Int!
    $filter: RedirectionFilterInput
    $sort: RedirectionSortInput
  ) {
    getRedirections(page: $page, limit: $limit, filter: $filter, sort: $sort) {
      items {
        ...RedirectionFields
      }
      totalCount
      page
      totalPages
    }
  }
  ${REDIRECTION_FIELDS}
`;

export const GET_USERS = gql`
  query GetUsers(
    $page: Int!
    $limit: Int!
    $filter: UserFilterInput
    $sort: UserSortInput
  ) {
    getUsers(page: $page, limit: $limit, filter: $filter, sort: $sort) {
      items {
        ...UserFields
      }
      totalCount
      page
      totalPages
    }
  }
  ${USER_FIELDS}
`;

export const GET_USER = gql`
  query GetUser($id: ID!) {
    getUser(id: $id) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const SEARCH_USERS = gql`
  query SearchUsers($query: String!) {
    searchUsers(query: $query) {
      id
      username
      name
      avatar
    }
  }
`;

export const GET_GROUPS = gql`
  query GetGroups(
    $page: Int!
    $limit: Int!
    $filter: GroupFilterInput
    $sort: GroupSortInput
  ) {
    getGroups(page: $page, limit: $limit, filter: $filter, sort: $sort) {
      items {
        ...GroupFields
      }
      totalCount
      page
      totalPages
    }
  }
  ${GROUP_FIELDS}
`;

export const GET_GROUP = gql`
  query GetGroup($id: ID!) {
    getGroup(id: $id) {
      ...GroupFields
    }
  }
  ${GROUP_FIELDS}
`;

/**
 * Paginated admin listing. Used by RolesTable.
 */
export const GET_ROLES = gql`
  query GetRoles(
    $page: Int!
    $limit: Int!
    $filter: RoleFilterInput
    $sort: RoleSortInput
  ) {
    getRoles(page: $page, limit: $limit, filter: $filter, sort: $sort) {
      items {
        ...RoleFields
      }
      totalCount
      page
      totalPages
    }
  }
  ${ROLE_FIELDS}
`;

/**
 * Unpaginated selector list. Used by UserModal / GroupMembersModal to
 * populate role pickers (they need the full active catalogue, usually
 * narrowed by resourceType).
 */
export const GET_ALL_ROLES = gql`
  query GetAllRoles($filter: RoleFilterInput) {
    getAllRoles(filter: $filter) {
      ...RoleFields
    }
  }
  ${ROLE_FIELDS}
`;

export const GET_ROLE = gql`
  query GetRole($id: ID!) {
    getRole(id: $id) {
      ...RoleFields
    }
  }
  ${ROLE_FIELDS}
`;

export const GET_BASE_URLS = gql`
  query GetBaseUrls {
    getBaseUrls {
      url
      count
    }
  }
`;

export const GET_STATS_OVERVIEW = gql`
  query GetStatsOverview($range: TimeRangeInput!, $configId: ID) {
    getStatsOverview(range: $range, configId: $configId) {
      totalLogs
      previousPeriodLogs
      activeTags
      totalTags
      topTag {
        configId
        alias
        tagIcon
        tagColor
        hitCount
      }
    }
  }
`;

export const GET_REDIRECTION_TIMELINE = gql`
  query GetRedirectionTimeline($range: TimeRangeInput!, $configId: ID) {
    getRedirectionTimeline(range: $range, configId: $configId) {
      timestamp
      redirectionConfig {
        ...RedirectionFields
      }
    }
  }
  ${REDIRECTION_FIELDS}
`;

export const GET_TOP_TAGS = gql`
  query GetTopTags($range: TimeRangeInput!, $limit: Int!) {
    getTopTags(range: $range, limit: $limit) {
      configId
      alias
      tagIcon
      tagColor
      hitCount
      sparkline
    }
  }
`;

export const GET_REDIRECTION_DISTRIBUTION = gql`
  query GetRedirectionDistribution($range: TimeRangeInput!, $configId: ID) {
    getRedirectionDistribution(range: $range, configId: $configId) {
      timestamp
      redirectionConfig {
        ...RedirectionFields
      }
    }
  }
  ${REDIRECTION_FIELDS}
`;

export const GET_RECENT_LOGS = gql`
  query GetRecentLogs($configId: ID!, $limit: Int!) {
    getRecentLogs(configId: $configId, limit: $limit) {
      timestamp
      userAgent
      ipAddress
    }
  }
`;

// ── Mutations ───────────────────────────────────────────────────────────────

export const LOGIN_MUTATION = gql`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      user {
        ...UserFields
      }
    }
  }
  ${USER_FIELDS}
`;

export const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout
  }
`;

// ── Redirections ────────────────────────────────────────────────────────────

export const CREATE_REDIRECTION = gql`
  mutation CreateRedirection($input: CreateRedirectionInput!) {
    createRedirection(input: $input) {
      ...RedirectionFields
    }
  }
  ${REDIRECTION_FIELDS}
`;

export const EDIT_REDIRECTION = gql`
  mutation EditRedirection($input: EditRedirectionInput!) {
    editRedirection(input: $input) {
      ...RedirectionFields
    }
  }
  ${REDIRECTION_FIELDS}
`;

export const DELETE_REDIRECTIONS = gql`
  mutation DeleteRedirections($ids: [ID!]!) {
    deleteRedirections(ids: $ids)
  }
`;

// ── Users ───────────────────────────────────────────────────────────────────

export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const DISABLE_USER = gql`
  mutation DisableUser($id: ID!, $isDisabled: Boolean!) {
    disableUser(id: $id, isDisabled: $isDisabled) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const DELETE_USERS = gql`
  mutation DeleteUsers($ids: [ID!]!) {
    deleteUsers(ids: $ids)
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input)
  }
`;

// ── Groups ──────────────────────────────────────────────────────────────────

export const CREATE_GROUP = gql`
  mutation CreateGroup($input: CreateGroupInput!) {
    createGroup(input: $input) {
      ...GroupFields
    }
  }
  ${GROUP_FIELDS}
`;

export const UPDATE_GROUP = gql`
  mutation UpdateGroup($input: UpdateGroupInput!) {
    updateGroup(input: $input) {
      ...GroupFields
    }
  }
  ${GROUP_FIELDS}
`;

export const DELETE_GROUP = gql`
  mutation DeleteGroup($id: ID!) {
    deleteGroup(id: $id)
  }
`;

export const ADD_GROUP_MEMBER = gql`
  mutation AddGroupMember($input: AddMemberInput!) {
    addGroupMember(input: $input) {
      ...GroupFields
    }
  }
  ${GROUP_FIELDS}
`;

export const UPDATE_GROUP_MEMBER = gql`
  mutation UpdateGroupMember($input: UpdateMemberInput!) {
    updateGroupMember(input: $input) {
      ...GroupFields
    }
  }
  ${GROUP_FIELDS}
`;

export const REMOVE_GROUP_MEMBER = gql`
  mutation RemoveGroupMember($input: RemoveMemberInput!) {
    removeGroupMember(input: $input) {
      ...GroupFields
    }
  }
  ${GROUP_FIELDS}
`;

export const SET_MEMBERSHIP_DISABLED = gql`
  mutation SetMembershipDisabled($input: SetMembershipDisabledInput!) {
    setMembershipDisabled(input: $input) {
      ...GroupFields
    }
  }
  ${GROUP_FIELDS}
`;

// ── Roles (super-admin only) ────────────────────────────────────────────────

export const CREATE_ROLE = gql`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      ...RoleFields
    }
  }
  ${ROLE_FIELDS}
`;

export const UPDATE_ROLE = gql`
  mutation UpdateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) {
      ...RoleFields
    }
  }
  ${ROLE_FIELDS}
`;

export const DELETE_ROLE = gql`
  mutation DeleteRole($id: ID!) {
    deleteRole(id: $id)
  }
`;
